import React,{useEffect,useMemo,useState} from 'react';
import {loadGeoPerformanceComparison} from './geoApi.js';

const number=value=>Number(value||0).toLocaleString('en-NG');
const maxOf=(rows,key)=>Math.max(1,...rows.map(item=>Number(item[key]||0)));

/** Loads only while expanded: heavy historical aggregation never blocks the opening globe. */
export default function GeoPerformanceComparison({role,token,refreshKey=0,onExploreState}){
  const [months,setMonths]=useState(6),[data,setData]=useState(null);
  const [busy,setBusy]=useState(false),[error,setError]=useState(''),[retry,setRetry]=useState(0);
  useEffect(()=>{
    if(!token){setData(null);return undefined;}
    const ctrl=new AbortController();setBusy(true);setError('');
    loadGeoPerformanceComparison(role,token,months,ctrl.signal).then(result=>{
      if(!ctrl.signal.aborted)setData(result);
    }).catch(err=>{
      if(!ctrl.signal.aborted)setError(err?.response?.data?.message||'Comparison data is unavailable.');
    }).finally(()=>{if(!ctrl.signal.aborted)setBusy(false);});
    return()=>ctrl.abort();
  },[role,token,months,refreshKey,retry]);
  const ready=data?.months===months;
  const series=useMemo(()=>ready?data.series||[]:[],[ready,data]);
  const peak=maxOf(series,'registrations');
  return <section className="ugi-compare" aria-label="Verified multi-month acquisition comparison">
    <div className="ugi-compare-heading">
      <div><span className="ugi-eyebrow">EXECUTIVE INTELLIGENCE · HISTORICAL</span><h4>Acquisition over time</h4>
        <p>Recorded client registrations versus verified FDC attribution, scoped to your permitted team.</p></div>
      <div className="ugi-compare-windows" role="group" aria-label="Choose historical comparison period">
        {[3,6,12].map(n=><button key={n} type="button" aria-pressed={months===n} onClick={()=>setMonths(n)}>{n} months</button>)}
      </div>
    </div>
    {error&&<div className="ugi-ops-error" role="alert">{error} <button type="button" onClick={()=>setRetry(x=>x+1)}>Retry</button></div>}
    {busy&&<p className="ugi-ops-wait" aria-live="polite">Reading authorized monthly acquisition records…</p>}
    {ready&&!busy&&<>
      <div className="ugi-compare-kpis">
        <div><small>Total registrations</small><strong>{number(data.summary?.registrations)}</strong></div>
        <div><small>Credited to FDCs</small><strong>{number(data.summary?.attributed)}</strong></div>
        <div><small>Unattributed / organic</small><strong>{number(data.summary?.unattributed)}</strong></div>
        <div><small>Visible consultants</small><strong>{number(data.summary?.fdcs)}</strong></div>
      </div>
      <div className="ugi-compare-chart" role="group" aria-label="Monthly registration comparison">
        {series.map(item=><div className="ugi-compare-month" key={item.month}>
          <div className="ugi-compare-columns" role="img" aria-label={`${item.month}: ${item.registrations} registrations, ${item.attributed} FDC credited`}>
            <div className="ugi-compare-track ugi-compare-total" style={{height:`${Math.round(item.registrations/peak*100)}%`}}/>
            <div className="ugi-compare-track ugi-compare-attributed" style={{height:`${Math.round(item.attributed/peak*100)}%`}}/>
          </div>
          <span className="ugi-compare-amount">{number(item.attributed)} <small>/ {number(item.registrations)}</small></span>
          <time>{item.month}</time>
        </div>)}
      </div>
      <div className="ugi-compare-legend"><span><i className="ugi-legend-total"/> All registrations</span><span><i className="ugi-legend-attributed"/> Credited FDC</span><span>Last month in the chart may be incomplete.</span></div>
      <div className="ugi-compare-tables">
        <div className="ugi-compare-list"><h4>FDC teams by assigned state</h4><p>Staff assignment region, not verified physical position. Unassigned staff are listed separately.</p>
          {data.teamStates?.length?data.teamStates.map(item=><div className="ugi-compare-list-row" key={item.code}>
            <span><b>{item.code==='UNASSIGNED'?'Unassigned':item.code}</b><small>{number(item.consultants)} FDCs</small></span><span><b>{number(item.current)}</b><small>Current month · {number(item.previous)} previous</small></span>
          </div>):<p>No FDC assignments in this reporting scope.</p>}
        </div>
        <div className="ugi-compare-list"><h4>Registrations by client state</h4><p>Only credited clients with a recognizable Nigerian state.</p>
          {data.clientStates?.length?data.clientStates.map(item=><button className="ugi-compare-state" key={item.code} type="button" disabled={!onExploreState} onClick={()=>onExploreState?.(item.code)}>
            <span><b>{item.code}</b><small>Inspect region ↗</small></span><span><b>{number(item.current)}</b><small>{number(item.previous)} previous</small></span>
          </button>):<p>No mapped credited registrations in these two months.</p>}
        </div>
      </div>
      <div className="ugi-perf-footnotes">{data.notes?.map((note,index)=><p key={index}>{note}</p>)}</div>
    </>}
  </section>;
}
